import React, { useState } from "react";
import { toast } from "sonner";
import { Lock, LockOpen, FileCheck2, AlertTriangle, CheckCircle2, Circle, BadgeCheck, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PasswordPromptDialog from "@/components/PasswordPromptDialog";
import { api } from "@/lib/api";

const errMsg = (e, fb) => e?.response?.data?.detail || fb;

export const isNfaOpen = (b) => !!b?.nfa_open;

export const NfaActivateButton = ({ budget, onChange }) => {
  const [warnOpen, setWarnOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const activate = async (password) => {
    try {
      await api.post(`/budgets/${budget.id}/nf-antecipada`, { password });
      toast.success("Orçamento travado para emissão da documentação");
      setPwdOpen(false);
      await onChange?.();
    } catch (e) {
      toast.error(errMsg(e, "Erro ao antecipar a nota"));
    }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setWarnOpen(true)} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="btn-nf-antecipada">
        <FileCheck2 className="w-4 h-4 mr-2" />
        Antecipar nota / documentação
      </Button>
      <AlertDialog open={warnOpen} onOpenChange={setWarnOpen}>
        <AlertDialogContent data-testid="nfa-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-600" />Emitir documentação antecipada</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-sm">
              <span className="block">Você está finalizando o orçamento Nº <strong>{budget.number}</strong> para emissão da documentação (nota fiscal, carta, concorrências), porém ele <strong>continuará em andamento</strong> até todos os itens serem concluídos.</span>
              <span className="block font-semibold text-rose-700">Você não poderá alterar itens, adicionar itens, excluir itens, nem editar valores.</span>
              <span className="block">Deseja continuar?</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="nfa-warning-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => setPwdOpen(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="nfa-warning-continue">Continuar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PasswordPromptDialog open={pwdOpen} onOpenChange={setPwdOpen} title="Confirmar nota antecipada" description="Informe a senha de administrador para travar o orçamento." onConfirm={activate} />
    </>
  );
};

const PendingList = ({ items }) => (
  <ul className="mt-3 space-y-1 max-h-56 overflow-y-auto pr-1" data-testid="nfa-items-list">
    {items.map((it, i) => (
      <li key={it.id} className="flex items-start gap-2 text-xs" data-testid={`nfa-item-${it.id}`}>
        {it.completed ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
        <span className={`line-clamp-2 ${it.completed ? "text-slate-400 line-through" : "text-slate-700"}`}>{i + 1}. {(it.description || "").replace(/\[\/?b\]/g, "")}</span>
      </li>
    ))}
  </ul>
);

export default function NfAntecipadaPanel({ budget, onChange, onOpenFiscal }) {
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  if (!isNfaOpen(budget)) return null;
  const items = budget.items || [];
  const done = items.filter((it) => it.completed).length;
  const allDone = items.length > 0 && done === items.length;
  const hasNf = !!budget.nf_number;
  const isPaid = budget.status === "nota_faturada";
  const isClosedStatus = ["nota_fiscal_gerada", "nota_faturada"].includes(budget.status);

  const close = async () => {
    setBusy(true);
    try {
      await api.post(`/budgets/${budget.id}/nf-antecipada/close`);
      toast.success("Acompanhamento encerrado");
      await onChange?.();
    } catch (e) {
      toast.error(errMsg(e, "Erro ao encerrar"));
    } finally { setBusy(false); }
  };

  const finalize = async (status) => {
    setBusy(true);
    try {
      await api.put(`/budgets/${budget.id}`, { status });
      toast.success(status === "nota_faturada" ? "Marcado como Nota Paga" : "Finalizado como Nota Gerada");
      await onChange?.();
    } catch (e) {
      toast.error(errMsg(e, "Erro ao finalizar"));
    } finally { setBusy(false); }
  };
  const unlock = async (password) => {
    try {
      await api.post(`/budgets/${budget.id}/nf-antecipada/unlock`, { password });
      toast.success("Orçamento destravado");
      setUnlockOpen(false);
      await onChange?.();
    } catch (e) {
      toast.error(errMsg(e, "Erro ao destravar"));
    }
  };

  return (
    <div className={`rounded-xl border-2 p-4 ${allDone ? "bg-emerald-50 border-emerald-300" : "bg-indigo-50 border-indigo-200"}`} data-testid="nfa-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Lock className={`w-5 h-5 shrink-0 mt-0.5 ${allDone ? "text-emerald-700" : "text-indigo-700"}`} />
          <div className="min-w-0">
            <p className="font-heading text-sm font-bold text-slate-900">Nota antecipada — orçamento travado</p>
            <p className="text-xs text-slate-600" data-testid="nfa-progress">
              <strong>{done} de {items.length}</strong> itens concluídos{hasNf ? ` · NF ${budget.nf_number}` : " · dados da nota ainda não gravados"}
              {isPaid && <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-bold bg-violet-600 text-white px-2 py-0.5 rounded-full" data-testid="nfa-paid-badge">NOTA PAGA</span>}
            </p>
          </div>
        </div>
        <button type="button" onClick={() => setUnlockOpen(true)} className="text-[11px] font-semibold text-slate-500 hover:text-rose-600 inline-flex items-center gap-1" data-testid="btn-nfa-unlock">
          <LockOpen className="w-3.5 h-3.5" /> Destravar
        </button>
      </div>
      <div className="mt-3 h-2 rounded-full bg-white overflow-hidden">
        <div className={`h-full transition-[width] duration-500 ${allDone ? "bg-emerald-500" : "bg-indigo-500"}`} style={{ width: `${items.length ? (done / items.length) * 100 : 0}%` }} />
      </div>
      {allDone && isClosedStatus ? (
        <div className="mt-4 bg-white rounded-lg border border-emerald-200 p-3" data-testid="nfa-close-alert">
          <p className="text-sm font-bold text-emerald-800 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />Todos os itens concluídos — a nota já está {isPaid ? "paga" : "gerada"}.</p>
          <p className="text-xs text-slate-600 mt-1">Encerre o acompanhamento para tirar o orçamento do grupo "Nota gerada — em andamento".</p>
          <Button size="sm" disabled={busy} onClick={close} className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-nfa-close"><CheckCircle2 className="w-4 h-4 mr-1.5" />Encerrar acompanhamento</Button>
        </div>
      ) : allDone ? (
        <div className="mt-4 bg-white rounded-lg border border-emerald-200 p-3" data-testid="nfa-finalize-alert">
          <p className="text-sm font-bold text-emerald-800 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Orçamento com nota já emitida — todos os itens concluídos.</p>
          <p className="text-xs text-slate-600 mt-1">Clique em finalizar para mudar o status para Nota Gerada ou Nota Paga, se for o caso.</p>
          {!hasNf && <p className="text-xs text-rose-600 mt-1">Grave os dados da nota antes de finalizar.</p>}
          <div className="flex flex-wrap gap-2 mt-3">
            {!hasNf && <Button size="sm" onClick={onOpenFiscal} className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="btn-nfa-gravar-nf"><Receipt className="w-4 h-4 mr-1.5" />Gravar dados da nota</Button>}
            <Button size="sm" disabled={!hasNf || busy} onClick={() => finalize("nota_fiscal_gerada")} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="btn-nfa-finalize-nfg"><FileCheck2 className="w-4 h-4 mr-1.5" />Finalizar — Nota Gerada</Button>
            <Button size="sm" disabled={!hasNf || busy} onClick={() => finalize("nota_faturada")} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="btn-nfa-finalize-nfp"><BadgeCheck className="w-4 h-4 mr-1.5" />Finalizar — Nota Paga</Button>
          </div>
        </div>
      ) : (
        <>
          {!isPaid && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" disabled={!hasNf || busy} onClick={() => setPayOpen(true)} className="border-violet-300 text-violet-700 hover:bg-violet-50 bg-white" data-testid="btn-nfa-mark-paid">
                <BadgeCheck className="w-4 h-4 mr-1.5" />Marcar como paga
              </Button>
              {!hasNf && <span className="text-[11px] text-slate-500">Grave os dados da nota para liberar.</span>}
            </div>
          )}
          <PendingList items={items} />
        </>
      )}
      <AlertDialog open={payOpen} onOpenChange={setPayOpen}>
        <AlertDialogContent data-testid="nfa-pay-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Marcar nota como paga?</AlertDialogTitle>
            <AlertDialogDescription>
              O orçamento Nº {budget.number} passa para <strong>Nota Fiscal Paga</strong> e entra no financeiro agora. Ele continua no grupo "Nota gerada — em andamento" até você concluir os {items.length - done} item(ns) pendente(s) e encerrar o acompanhamento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => finalize("nota_faturada")} className="bg-violet-600 hover:bg-violet-700" data-testid="nfa-pay-confirm">Confirmar pagamento</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PasswordPromptDialog open={unlockOpen} onOpenChange={setUnlockOpen} title="Destravar orçamento" description="Isso libera a edição de itens e valores. Informe a senha de administrador." onConfirm={unlock} />
    </div>
  );
}
